import { useAuth } from '@berky/auth'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@berky/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Camera, Lightbulb, LoaderCircle, MessageSquareText } from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

import { captureVisiblePortal } from './capture'
import { useFeedbackApi } from './feedback-api'
import {
  feedbackCategories,
  feedbackCategoryLabels,
  feedbackCategoryPrompts,
  type FeedbackCategory,
} from './feedback-category'
import { FeedbackReference } from './feedback-reference'
import { FeedbackReminder } from './feedback-reminder'
import { collectFeedbackPageContext } from './page-context'
import { ScreenshotEditor, type ScreenshotEditorHandle } from './screenshot-editor'
import type { FeedbackPageContext, FeedbackSubmission } from './types'
import { useFeedbackReminder } from './use-feedback-reminder'

type FeedbackDraft =
  | {
      art: 'Seitenfeedback'
      context: FeedbackPageContext
      screenshot: HTMLCanvasElement
    }
  | {
      art: 'AllgemeineIdee'
      buildId: string
    }

export function FeedbackTab() {
  const { user } = useAuth()
  const api = useFeedbackApi()
  const queryClient = useQueryClient()
  const editorRef = useRef<ScreenshotEditorHandle>(null)
  const chooserRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const hadDraftRef = useRef(false)
  const [draft, setDraft] = useState<FeedbackDraft | null>(null)
  const [text, setText] = useState('')
  const [category, setCategory] = useState<FeedbackCategory | null>(null)
  const [chooserOpen, setChooserOpen] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const reminder = useFeedbackReminder(chooserOpen || draft !== null || isCapturing || isSubmitting)

  useEffect(() => {
    if (draft) {
      hadDraftRef.current = true
    } else if (hadDraftRef.current) {
      hadDraftRef.current = false
      requestAnimationFrame(() => triggerRef.current?.focus())
    }
  }, [draft])

  useEffect(() => {
    if (!chooserOpen) return

    function closeOnOutsideClick(event: PointerEvent) {
      const target = event.target
      if (!(target instanceof Node)) return
      if (chooserRef.current?.contains(target) || triggerRef.current?.contains(target))
        return

      setChooserOpen(false)
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopImmediatePropagation()
      setChooserOpen(false)
    }

    function closeOnScroll() {
      setChooserOpen(false)
    }

    document.addEventListener('pointerdown', closeOnOutsideClick)
    window.addEventListener('keydown', closeOnEscape, true)
    window.addEventListener('scroll', closeOnScroll, true)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      window.removeEventListener('keydown', closeOnEscape, true)
      window.removeEventListener('scroll', closeOnScroll, true)
    }
  }, [chooserOpen])

  if (!user) return null

  async function startPageFeedback() {
    setChooserOpen(false)
    setIsCapturing(true)
    const context = collectFeedbackPageContext()
    try {
      const screenshot = await captureVisiblePortal()
      setText('')
      setCategory(null)
      setDraft({ art: 'Seitenfeedback', context, screenshot })
    } catch (error) {
      toast.error('Screenshot nicht möglich', {
        description:
          error instanceof Error
            ? error.message
            : 'Der aktuelle Bildschirm konnte nicht aufgenommen werden.',
      })
    } finally {
      setIsCapturing(false)
    }
  }

  function startGeneralIdea() {
    setChooserOpen(false)
    setText('')
    setCategory(null)
    setDraft({ art: 'AllgemeineIdee', buildId: collectFeedbackPageContext().buildId })
  }

  async function submitFeedback(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!draft || !text.trim()) return

    setIsSubmitting(true)
    try {
      const submission: FeedbackSubmission =
        draft.art === 'Seitenfeedback'
          ? await seitenfeedbackErstellen(draft, text, editorRef)
          : {
              text: text.trim(),
              art: 'AllgemeineIdee',
              seitenPfad: '/',
              modulId: null,
              seitentitel: 'Allgemeine Idee',
              buildId: draft.buildId,
            }
      const saved = await api.post<{ id: string; reference: string }>('/eintraege', {
        ...submission,
        category,
      })
      reminder.suppress()
      void queryClient.invalidateQueries({ queryKey: ['feedback'] })
      setDraft(null)
      toast.success('Feedback gesendet', {
        description: (
          <FeedbackReference
            reference={saved.reference}
            id={saved.id}
            management={false}
          />
        ),
        duration: 10_000,
      })
    } catch (error) {
      toast.error('Feedback konnte nicht gesendet werden', {
        description:
          error instanceof Error
            ? error.message
            : 'Bitte versuchen Sie es in einem Moment noch einmal.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const feedbackLauncher = (
    <>
      {reminder.visible && (
        <FeedbackReminder onClick={() => {
          reminder.dismiss()
          setChooserOpen(true)
        }} />
      )}
      {chooserOpen && (
        <div
          ref={chooserRef}
          role="dialog"
          aria-label="Art des Feedbacks auswählen"
          data-overlay-keep-open
          data-feedback-capture-ignore
          className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-[60] flex max-w-[calc(100vw-2rem)] flex-col items-end gap-3 md:right-6"
        >
          <FeedbackChoice
            icon={<Lightbulb aria-hidden="true" />}
            title="Allgemeine Idee für Berky-Digital"
            onClick={startGeneralIdea}
          />
          <FeedbackChoice
            icon={<Camera aria-hidden="true" />}
            title="Feedback zu diesem Modul"
            onClick={() => void startPageFeedback()}
          />
        </div>
      )}
      <Button
        ref={triggerRef}
        type="button"
        variant="default"
        size="icon"
        disabled={isCapturing}
        onClick={() => {
          if (reminder.visible) reminder.dismiss()
          setChooserOpen((open) => !open)
        }}
        data-feedback-capture-ignore
        data-overlay-keep-open
        aria-busy={isCapturing}
        aria-expanded={chooserOpen}
        aria-label={isCapturing ? 'Bildschirm wird aufgenommen' : 'Feedback geben'}
        className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 z-[60] size-12 rounded-full p-0 shadow-lg md:right-6"
      >
        {isCapturing ? (
          <LoaderCircle aria-hidden="true" className="size-5 animate-spin" />
        ) : (
          <MessageSquareText aria-hidden="true" className="size-5" />
        )}
      </Button>
    </>
  )

  return (
    <>
      {draft === null &&
        createPortal(
          <div
            data-feedback-layer
            data-overlay-keep-open
            data-feedback-capture-ignore
            className="contents"
          >
            {feedbackLauncher}
          </div>,
          document.body,
        )}

      <Dialog
        open={draft !== null}
        onOpenChange={(open) => {
          if (!open && !isSubmitting) setDraft(null)
        }}
      >
        {draft && (
          <DialogContent
            data-feedback-capture-ignore
            data-feedback-dialog
            data-overlay-keep-open
            className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-5xl"
          >
            <form className="grid gap-5" onSubmit={(event) => void submitFeedback(event)}>
              <DialogHeader>
                <DialogTitle>
                  {draft.art === 'AllgemeineIdee'
                    ? 'Allgemeine Idee zu Berky Digital'
                    : 'Feedback zu dieser Seite'}
                </DialogTitle>
                <DialogDescription>
                  {draft.art === 'AllgemeineIdee'
                    ? 'Beschreiben Sie Ihre Idee kurz. Sie wird ohne Screenshot und Modulbezug gespeichert.'
                    : 'Beschreiben Sie kurz Ihren Vorschlag. Screenshot und Seitenkontext werden automatisch mitgesendet.'}
                </DialogDescription>
              </DialogHeader>

              {draft.art === 'Seitenfeedback' ? (
                <>
                  <Alert>
                    <Camera aria-hidden="true" />
                    <AlertTitle>Automatisch erfasster Kontext</AlertTitle>
                    <AlertDescription>
                      <span className="block">
                        {draft.context.seitentitel} · {draft.context.seitenPfad}
                      </span>
                      <span className="block">
                        {draft.context.modulTitel
                          ? `Modul ${draft.context.modulTitel}`
                          : 'Portal-Basis'}{' '}
                        · Build {draft.context.buildId} · {user.name} ({user.email})
                      </span>
                    </AlertDescription>
                  </Alert>
                  <ScreenshotEditor ref={editorRef} screenshot={draft.screenshot} />
                </>
              ) : (
                <Alert>
                  <Lightbulb aria-hidden="true" />
                  <AlertTitle>Allgemeine Idee</AlertTitle>
                  <AlertDescription>
                    Ihre Idee wird dem Bereich Allgemeine Ideen zugeordnet.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="feedback-category">Kategorie (optional)</Label>
                <Select
                  value={category ?? 'none'}
                  onValueChange={(value) =>
                    setCategory(feedbackCategories.find((item) => item === value) ?? null)
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="feedback-category" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ohne Kategorie</SelectItem>
                    {feedbackCategories.map((value) => (
                      <SelectItem key={value} value={value}>
                        {feedbackCategoryLabels[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {category && (
                  <p
                    id="feedback-category-help"
                    className="text-sm text-muted-foreground"
                  >
                    {feedbackCategoryPrompts[category]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="feedback-text">
                  {draft.art === 'AllgemeineIdee'
                    ? 'Was ist Ihre Idee?'
                    : 'Was möchten Sie ändern oder vorschlagen?'}
                </Label>
                <Textarea
                  id="feedback-text"
                  aria-describedby={category ? 'feedback-category-help' : undefined}
                  required
                  maxLength={2000}
                  rows={4}
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  placeholder={
                    draft.art === 'AllgemeineIdee'
                      ? 'Beschreiben Sie Ihre Idee kurz und konkret.'
                      : 'Beschreiben Sie Ihr Feedback kurz und konkret.'
                  }
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  {`Pflichtfeld · ${text.length.toLocaleString('de-DE')} von 2.000 Zeichen`}
                </p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => setDraft(null)}
                >
                  Abbrechen
                </Button>
                <Button type="submit" disabled={isSubmitting || !text.trim()}>
                  {isSubmitting && (
                    <LoaderCircle aria-hidden="true" className="animate-spin" />
                  )}
                  {draft.art === 'AllgemeineIdee' ? 'Idee senden' : 'Feedback senden'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        )}
      </Dialog>
    </>
  )
}

async function seitenfeedbackErstellen(
  draft: Extract<FeedbackDraft, { art: 'Seitenfeedback' }>,
  text: string,
  editorRef: RefObject<ScreenshotEditorHandle | null>,
): Promise<FeedbackSubmission> {
  if (!editorRef.current) throw new Error('Der Screenshot ist noch nicht bereit.')

  const screenshot = await editorRef.current.exportScreenshot()
  return {
    text: text.trim(),
    art: 'Seitenfeedback',
    seitenPfad: draft.context.seitenPfad,
    modulId: draft.context.modulId,
    seitentitel: draft.context.seitentitel,
    buildId: draft.context.buildId,
    screenshotContentType: screenshot.contentType,
    screenshotDaten: screenshot.datenBase64,
    screenshotBreite: screenshot.breite,
    screenshotHoehe: screenshot.hoehe,
  }
}

function FeedbackChoice({
  icon,
  title,
  onClick,
}: {
  icon: ReactNode
  title: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className="group flex items-center text-right focus-visible:outline-none"
      aria-label={title}
      onClick={onClick}
    >
      <span className="rounded-lg border bg-background px-3 py-2 pr-7 text-sm font-medium shadow-sm transition-colors group-hover:bg-accent group-focus-visible:bg-accent">
        {title}
      </span>
      <span className="relative -ml-4 flex size-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-colors group-hover:bg-primary/90 group-focus-visible:bg-primary/90">
        {icon}
      </span>
    </button>
  )
}
